/**
 * Calendar Mappings Hooks
 *
 * React Query hooks for calendar mapping management (Map & Shoot workflow)
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { calendarMappingsService } from '@/services/calendar-mappings.service';
import {
  CalendarViewParams,
  CreateMappingData,
  MoveDateData,
  MoveCalendarData,
  ApplyRulesData,
  CalendarMapping,
  CalendarMappingsView,
} from '@/types/calendar-mappings';
import { useInvalidateRelease } from '@/lib/invalidateRelease';

/**
 * Fetch calendar view (all mappings for a month, grouped by date)
 */
export const useCalendarView = (params: CalendarViewParams) =>
  useQuery({
    queryKey: ['calendar-mappings', 'view', params],
    queryFn: () => calendarMappingsService.getCalendarView(params),
    enabled: !!params.calendarId && !!params.month,
  });

/**
 * Fetch single mapping by ID
 */
export const useCalendarMapping = (id: number) =>
  useQuery({
    queryKey: ['calendar-mappings', id],
    queryFn: () => calendarMappingsService.getMapping(id),
    enabled: !!id,
  });

/**
 * Create new mapping (map post/variation to calendar)
 */
export const useCreateMapping = () => {
  const queryClient = useQueryClient();
  const invalidateRelease = useInvalidateRelease();

  return useMutation({
    mutationFn: (data: CreateMappingData) => calendarMappingsService.createMapping(data),
    onSuccess: (newMapping) => {
      // Cache sync (calendar views + all release surfaces)
      invalidateRelease();

      // Optionally: Update cache optimistically
      const date = new Date(newMapping.plannedDate).toISOString().split('T')[0];
      queryClient.setQueryData<CalendarMappingsView>(
        ['calendar-mappings', 'view', { calendarId: newMapping.calendarId, month: date.slice(0, 7) }],
        (oldData: CalendarMappingsView | undefined) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            [date]: [...(oldData[date] || []), newMapping],
          };
        }
      );
    },
  });
};

/**
 * Move mapping to different date (drag-and-drop)
 */
export const useMoveDate = () => {
  const queryClient = useQueryClient();
  const invalidateRelease = useInvalidateRelease();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: MoveDateData }) =>
      calendarMappingsService.moveDate(id, data),
    onMutate: async ({ id, data }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['calendar-mappings', 'view'] });

      // Snapshot previous value
      const previousViews = queryClient.getQueriesData({ queryKey: ['calendar-mappings', 'view'] });

      // Optimistically update cache
      queryClient.setQueriesData<CalendarMappingsView>(
        { queryKey: ['calendar-mappings', 'view'] },
        (oldData: CalendarMappingsView | undefined) => {
          if (!oldData) return oldData;

          const newDate = new Date(data.newDate).toISOString().split('T')[0];
          const updatedData = { ...oldData };

          // Find and remove the mapping from its old date
          let movedMapping: CalendarMapping | undefined;
          for (const date in updatedData) {
            const index = updatedData[date].findIndex((m: CalendarMapping) => m.id === id);
            if (index !== -1) {
              [movedMapping] = updatedData[date].splice(index, 1);
              if (updatedData[date].length === 0) {
                delete updatedData[date];
              }
              break;
            }
          }

          // Add to new date
          if (movedMapping) {
            updatedData[newDate] = [
              ...(updatedData[newDate] || []),
              { ...movedMapping, plannedDate: newDate },
            ];
          }

          return updatedData;
        }
      );

      return { previousViews };
    },
    onError: (_error, _variables, context) => {
      // Rollback on error
      if (context?.previousViews) {
        context.previousViews.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSettled: () => {
      // Refetch to ensure consistency
      // Calendar grid + sidebar + board must follow the move
      invalidateRelease();
    },
  });
};

/**
 * Move mapping to different calendar
 */
export const useMoveCalendar = () => {
  const queryClient = useQueryClient();
  const invalidateRelease = useInvalidateRelease();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: MoveCalendarData }) =>
      calendarMappingsService.moveCalendar(id, data),
    onMutate: async ({ id }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['calendar-mappings', 'view'] });

      // Snapshot previous value
      const previousViews = queryClient.getQueriesData({ queryKey: ['calendar-mappings', 'view'] });

      // Optimistically remove from current calendar view
      queryClient.setQueriesData<CalendarMappingsView>(
        { queryKey: ['calendar-mappings', 'view'] },
        (oldData: CalendarMappingsView | undefined) => {
          if (!oldData) return oldData;

          const updatedData = { ...oldData };

          // Remove the mapping from all dates
          for (const date in updatedData) {
            updatedData[date] = updatedData[date].filter((m: CalendarMapping) => m.id !== id);
            if (updatedData[date].length === 0) {
              delete updatedData[date];
            }
          }

          return updatedData;
        }
      );

      return { previousViews };
    },
    onError: (_error, _variables, context) => {
      // Rollback on error
      if (context?.previousViews) {
        context.previousViews.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSettled: () => {
      // Refetch all calendar views (mapping moved between calendars)
      // Calendar grid + sidebar + board must follow the move
      invalidateRelease();
    },
  });
};

/**
 * Delete mapping
 */
export const useDeleteMapping = () => {
  const invalidateRelease = useInvalidateRelease();

  return useMutation({
    mutationFn: ({ id }: { id: number; teamId: number }) =>
      calendarMappingsService.deleteMapping(id),
    onSuccess: () => {
      // Invalidate calendar views

      invalidateRelease();
    },
  });
};

/**
 * Mark mapping as ready to shoot
 */
export const useMarkReadyToShoot = () => {
  const queryClient = useQueryClient();
  const invalidateRelease = useInvalidateRelease();

  return useMutation({
    mutationFn: (id: number) => calendarMappingsService.markReadyToShoot(id),
    onSuccess: (updatedMapping) => {
      // Update specific mapping in cache
      queryClient.setQueryData(['calendar-mappings', updatedMapping.id], updatedMapping);

      // Invalidate calendar views to show updated status

      invalidateRelease();
    },
  });
};

/**
 * Apply mapping rules (auto-distribute variations)
 */
export const useApplyMappingRules = () => {
  const invalidateRelease = useInvalidateRelease();

  return useMutation({
    mutationFn: (data: ApplyRulesData) => calendarMappingsService.applyMappingRules(data),
    onSuccess: () => {
      // Invalidate all calendar views (multiple mappings created)

      invalidateRelease();
    },
  });
};
